/* eslint-disable no-useless-escape */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Message } from '../../services/openai.service';
import SemanticSearchService, { Company } from '../../services/semantic-search.service';
import CityService, { City } from '../../services/city.service';
import ServiceMappingService, { ConversationMessage } from '../../services/service-mapping.service';
import CompanyService from '../../services/company.service';
import { useWhatsApp } from '../../context/WhatsAppContext';
import { useAuth } from '../../context/AuthContext';
import CityCards from './CityCards';
import CompanyCards from './CompanyCards';
import CompanyContactCard from './CompanyContactCard';
import { dateTimePatterns, locationPhrases, searchPatterns, searchPhrases } from '../../utils/ConstantsUtils';

// Variables globales para rastrear estados de la conversación
let citiesShownInCurrentConversation = false;
let companiesShownInCurrentConversation = false;
let contactCardShownInCurrentConversation = false;
let userApprovedCompany = false; // Variable para rastrear si el usuario aprobó una empresa
let phoneNumberProvided = false; // Variable para rastrear si el usuario ya proporcionó un número de teléfono
let userPhoneNumber = ''; // Variable para almacenar el número de teléfono capturado del usuario

// Variables para almacenar la información de la empresa seleccionada
let selectedCompanyData: {
  companyId: string;
  companyName: string;
  phoneNumber: string;
  profileImage?: string;
} | null = null;

// Función para marcar que el usuario ha aprobado una empresa
export const setUserApprovedCompany = (approved: boolean) => {
  userApprovedCompany = approved;
};

// Función para resetear el estado de las ciudades mostradas
export const resetCitiesShownState = () => {
  citiesShownInCurrentConversation = false;
  companiesShownInCurrentConversation = false;
  contactCardShownInCurrentConversation = false;
  selectedCompanyData = null;
  userApprovedCompany = false;
  phoneNumberProvided = false; // Resetear también el estado del teléfono
  userPhoneNumber = ''; // Resetear el número de teléfono capturado
};

// Función para obtener el número de teléfono capturado del usuario
export const getUserPhoneNumber = (): string => {
  return userPhoneNumber;
};

interface AgentMessageProps {
  content: string;
  timestamp?: string;
  onSendMessage?: (message: string) => void;
  conversationId?: string | null;
  allMessages?: Message[]; // Todos los mensajes de la conversación
  onShowContactCard?: () => void; // Callback para notificar cuando se muestra la tarjeta de contacto
}

const AgentMessage: React.FC<AgentMessageProps> = ({
  content,
  timestamp,
  onSendMessage,
  conversationId,
  allMessages,
  onShowContactCard,
}) => {
  const [copied, setCopied] = useState(false);
  const { userInfo } = useAuth();
  const { updateWhatsAppData } = useWhatsApp();
  const [showCityCards, setShowCityCards] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [cityError, setCityError] = useState<string | null>(null);
  const [contentByDefault, setContentByDefault] = useState<string>('');

  // Estados para empresas/profesionales
  const [showCompanyCards, setShowCompanyCards] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [serviceDescription, setServiceDescription] = useState<string>('');
  const [categoryName, setCategoryName] = useState<string>('');

  // Estados para la tarjeta de contacto
  const [showContactCard, setShowContactCard] = useState(false);
  const [companyContactInfo, setCompanyContactInfo] = useState<{
    companyName: string;
    phoneNumber: string;
    profileImage?: string;
  } | null>(null);

  // Estado para mostrar un loader mientras se buscan empresas
  const [showSearchingLoader, setShowSearchingLoader] = useState(false);

  // Efecto para mostrar la tarjeta de contacto si hay una empresa seleccionada
  useEffect(() => {
    return () => {
      if (selectedCompanyData && !contactCardShownInCurrentConversation) {
        let shouldShowContactCard = userApprovedCompany;

        if (!shouldShowContactCard && allMessages && allMessages.length >= 2) {
          for (let i = allMessages.length - 3; i >= 0; i--) {
            const msg = allMessages[i];
            if (msg.role === 'user' &&
              (msg.content.toLowerCase().includes('quiero contratar') ||
                msg.content.toLowerCase().includes(selectedCompanyData.companyName.toLowerCase()))) {
              shouldShowContactCard = true;
              break;
            }
          }
        }

        // Verificar si el mensaje actual es del agente Y es el último mensaje de la conversación
        const isCurrentMessageFromAgent = allMessages && allMessages.length > 0 &&
          allMessages[allMessages.length - 1]?.role === 'assistant';

        // Verificar si este mensaje es el último mensaje del agente en la conversación
        const isLastAgentMessage = isCurrentMessageFromAgent &&
          allMessages &&
          allMessages.length > 0 &&
          allMessages[allMessages.length - 1]?.content === content;

        // Mostrar la tarjeta SOLO si este es el último mensaje del agente en la conversación
        if (shouldShowContactCard && isLastAgentMessage) {
          const phoneNumber = selectedCompanyData.phoneNumber || '+57 300 123 4567';

          setCompanyContactInfo({
            companyName: selectedCompanyData.companyName,
            phoneNumber: phoneNumber,
            profileImage: selectedCompanyData.profileImage
          });

          updateWhatsAppData({
            To: phoneNumber
          });
          // Mostrar la tarjeta
          setShowContactCard(true);
          contactCardShownInCurrentConversation = true;
          setShowSearchingLoader(false); // Ocultar el loader cuando se muestra la tarjeta de contacto

          // Sumar un lead a la empresa seleccionada
          if (selectedCompanyData.companyId) {
            CompanyService.addLead(selectedCompanyData.companyId).then(response => {
              if (onShowContactCard) {
                setTimeout(() => {
                  onShowContactCard();
                }, 500);
              }
            }).catch(error => {
              if (onShowContactCard) {
                setTimeout(() => {
                  onShowContactCard();
                }, 500);
              }
            });
          } else {
            if (onShowContactCard) {
              setTimeout(() => {
                onShowContactCard();
              }, 500);
            }
          }
        }
      }
    }
  }, [allMessages]);

  // Detectar si el mensaje del agente está solicitando la ubicación o buscando profesionales
  useEffect(() => {
    const contentLower = content.toLowerCase();

    // Verificar solicitud de ubicación
    if (!citiesShownInCurrentConversation) {
      let isLocationRequest = locationPhrases.some(phrase => contentLower.includes(phrase.toLowerCase()));

      if (!isLocationRequest) {
        const locationPatterns = [
          /necesito\s+(?:que\s+me\s+)?(?:indiques|digas|menciones|informes)\s+(?:cu[aá]l\s+es\s+)?(?:tu|la)\s+(?:ubicaci[oó]n|ciudad|localidad)/i,
          /para\s+continuar\s+(?:,)?\s*necesito\s+(?:que\s+me\s+)?(?:indiques|digas|menciones|informes)/i,
          /(?:cu[aá]l|donde|d[oó]nde)\s+(?:es|ser[ií]a|est[aá]s|te\s+encuentras|vives|resides)/i,
          /(?:ubicaci[oó]n|ciudad|localidad)\s+(?:donde|en\s+(?:la\s+)?que)\s+(?:est[aá]s|te\s+encuentras|vives|resides)/i
        ];

        for (const pattern of locationPatterns) {
          if (pattern.test(contentLower)) {
            isLocationRequest = true;
            break;
          }
        }
      }
      // Si aún no se ha detectado, buscar palabras clave
      if (!isLocationRequest) {
        const locationKeywords = ['ubicación', 'ciudad', 'localidad', 'donde', 'dónde', 'indiques', 'indicar'];
        const keywordCount = locationKeywords.filter(keyword => contentLower.includes(keyword.toLowerCase())).length;
        if (keywordCount >= 2) isLocationRequest = true;
      }

      if (isLocationRequest) {
        fetchCities();
        return; // Si es una solicitud de ubicación, no seguir verificando
      }
    }

    // Verificar búsqueda de profesionales/empresas
    if (!companiesShownInCurrentConversation && conversationId) {
      // NUEVA LÓGICA: Verificar si el usuario ya proporcionó un número de teléfono
      if (!phoneNumberProvided && allMessages) {
        const hasPhoneNumber = detectAndExtractPhoneNumber(allMessages);
        if (hasPhoneNumber) {
          phoneNumberProvided = true;
        } else {
          setShowSearchingLoader(false);
          return;
        }
      }
      // Solo continuar con la lógica de búsqueda si ya tenemos el número de teléfono
      if (!phoneNumberProvided) {
        setShowSearchingLoader(false);
        return;
      }
      // Verificar si alguna de las frases está contenida en el mensaje
      let isSearchRequest = false;
      let matchedPhrase = '';

      // Activar el loader de búsqueda si el mensaje sugiere que se están buscando empresas
      setShowSearchingLoader(true);

      for (const phrase of searchPhrases) {
        if (contentLower.includes(phrase.toLowerCase())) {
          isSearchRequest = true;
          matchedPhrase = phrase;
          break;
        }
      }

      // Si no se encontró una frase exacta, buscar palabras clave
      if (!isSearchRequest) {
        // Verificar si el mensaje contiene la frase exacta que mencionó el usuario
        if (contentLower.includes('cambiaré tu solicitud') && contentLower.includes('mejores opciones')) {
          isSearchRequest = true;
          matchedPhrase = 'cambiaré tu solicitud + mejores opciones';
        } else if (contentLower.includes('permíteme un momento mientras busco')) {
          isSearchRequest = true;
          matchedPhrase = 'permíteme un momento mientras busco';
        } else if (contentLower.includes('no hay problema, cambiaré tu solicitud')) {
          isSearchRequest = true;
          matchedPhrase = 'no hay problema, cambiaré tu solicitud';
        } else {
          const strongKeywords = ['empresas', 'profesionales', 'busco', 'buscaré', 'buscando', 'mostraré', 'opciones'];
          const weakKeywords = ['disponibles', 'momento', 'mejores', 'cambiaré', 'solicitud'];

          const strongMatches = strongKeywords.filter(keyword => contentLower.includes(keyword.toLowerCase()));
          const weakMatches = weakKeywords.filter(keyword => contentLower.includes(keyword.toLowerCase()));

          // Para activar la búsqueda necesitamos al menos una palabra clave fuerte
          // o al menos tres palabras clave débiles
          if (strongMatches.length >= 1 || weakMatches.length >= 3) {
            // Excluir casos donde el agente está preguntando por fecha y hora
            const askingForDateTime = (
              contentLower.includes('qué día') ||
              contentLower.includes('qué hora') ||
              contentLower.includes('para cuándo') ||
              contentLower.includes('cuándo necesitas') ||
              (contentLower.includes('día') && contentLower.includes('hora'))
            );

            if (!askingForDateTime) {
              isSearchRequest = true;
              matchedPhrase = [...strongMatches, ...weakMatches].join(', ');
            }
          }

          let searchPatternDetected = false;
          if (!isSearchRequest) {
            for (const pattern of searchPatterns) {
              if (pattern.test(contentLower)) {
                searchPatternDetected = true;
                isSearchRequest = true;
                matchedPhrase = `patrón: ${pattern}`;
                break;
              }
            }
          }

          if (!searchPatternDetected) {
            let isDateTimeQuestion = false;
            for (const pattern of dateTimePatterns) {
              if (pattern.test(contentLower)) {
                isDateTimeQuestion = true;
                break;
              }
            }

            if (isDateTimeQuestion) isSearchRequest = false;
          }
        }
      }

      if (isSearchRequest) {
        setShowSearchingLoader(true);
        // Como extractSearchInfo es ahora asíncrona, la ejecutamos y manejamos cualquier error
        extractSearchInfo(content).catch(error => {
          setCompanyError('No se pudieron cargar los profesionales disponibles');
          setLoadingCompanies(false);
          setShowSearchingLoader(false);
        });
      } else {
        setShowSearchingLoader(false);
      }
    }
  }, [content, conversationId]);

  // Extraer información para la búsqueda semántica
  const extractSearchInfo = async (messageContent: string) => {
    let cityName = selectedCity || '';
    // Siempre intentar la búsqueda si tenemos el conversationId y los mensajes de la conversación
    if (conversationId && allMessages && allMessages.length > 0) {
      try {
        setLoadingCompanies(true);
        setCompanyError(null);

        // Convertir los mensajes al formato requerido por el servicio de mapeo
        const conversationMessages: ConversationMessage[] = allMessages.map(msg => ({
          role: msg.role === 'user' ? 'user' : 'assistant',
          content: msg.content,
          timestamp: new Date().toISOString() // Crear timestamp actual ya que no existe en Message
        }));

        // Llamar al servicio de mapeo con la conversación completa
        const mappingResponse = await ServiceMappingService.mapService(conversationId, conversationMessages);
        const exactServiceName = mappingResponse.serviceDescription;
        const exactCategoryName = mappingResponse.categoryName;
        const detectedCity = mappingResponse.cityName || cityName;

        if (!exactServiceName) {
          throw new Error('El servicio de mapeo no devolvió un nombre de servicio válido');
        }

        setServiceDescription(exactServiceName);
        setCategoryName(exactCategoryName);
        setSelectedCity(detectedCity || cityName);

        // Actualizar los datos para el mensaje de WhatsApp
        if (userInfo) {
          const scheduledDateTime = mappingResponse.scheduledDateTime;
          updateWhatsAppData({
            ServiceName: exactServiceName,
            CityName: detectedCity || cityName,
            DateTime: scheduledDateTime, // Usar directamente la fecha ISO del servicio de mapeo si existe
            UserRequestName: `${userInfo.name} ${userInfo.lastname}`,
            UserRequestEmail: userInfo.email
          });
        }

        // Realizar la búsqueda semántica con los datos exactos
        fetchCompanies(
          conversationId,
          exactServiceName,
          detectedCity || cityName,
          exactCategoryName
        );
      } catch (error) {
        setCompanyError('No se pudieron cargar los profesionales disponibles');
        setLoadingCompanies(false);
        setShowSearchingLoader(false); // Ocultar el loader de búsqueda en caso de error
      }
    } else {
      setShowSearchingLoader(false); // Ocultar el loader si no hay conversationId
    }
  };

  // Función para obtener las ciudades del API
  const fetchCities = async () => {
    try {
      setLoadingCities(true);
      setCityError(null);
      const citiesData = await CityService.getAllCities();
      setCities(citiesData);
      setShowCityCards(true);
      setLoadingCities(false);

      // Marcar que las ciudades ya se mostraron en esta conversación
      citiesShownInCurrentConversation = true;
    } catch (error) {
      setCityError('No se pudieron cargar las ciudades disponibles');
      setLoadingCities(false);
    }
  };

  // Función para obtener las empresas/profesionales del API
  const fetchCompanies = async (conversationId: string, serviceDesc: string, city: string, category?: string) => {
    try {
      setLoadingCompanies(true);
      setCompanyError(null);

      const searchRequest = {
        serviceDescription: serviceDesc,
        cityName: city,
        categoryName: category || undefined // Solo incluir si tiene valor
      };

      await new Promise(resolve => setTimeout(resolve, 100));
      const companiesData = await SemanticSearchService.searchCompanies(
        conversationId,
        searchRequest
      );
      if (companiesData && companiesData.length > 0) handleCompanySuccess(companiesData);
      else handleCompanyError();
    } catch (error) {
      setCompanyError('No se pudieron cargar los profesionales disponibles');
      setLoadingCompanies(false);
    }
  };

  const handleCompanyError = () => {
    setContentByDefault('¡Ups! Todos nuestros profesionales están ocupados en este momento. Estamos trabajando para conseguirte ayuda lo antes posible, o si lo deseas intenta nuevamente en unos minutos.');
    setShowCompanyCards(false);
    setShowSearchingLoader(false);
    setCompanyError('No se encontraron profesionales disponibles para tu búsqueda');
    setLoadingCompanies(false);
  }

  const handleCompanySuccess = (companiesData: Company[]) => {
    setCompanies(companiesData);
    setShowCompanyCards(true);
    companiesShownInCurrentConversation = true;
    setShowSearchingLoader(false);
    setLoadingCompanies(false);
  }

  // Función para detectar y extraer el número de teléfono del usuario
  const detectAndExtractPhoneNumber = (messages: Message[]): boolean => {
    if (!messages || messages.length === 0) return false;
    // Buscar en los últimos mensajes del usuario
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message.role === 'user') {
        const content = message.content;

        // Patrones para detectar números de teléfono colombianos
        const phonePatterns = [
          /(?:\+57\s?)?(?:3[0-9]{2}|31[0-9]|32[0-9]|35[0-9])\s?[0-9]{3}\s?[0-9]{4}/g, // Celulares colombianos
          /(?:\+57\s?)?[0-9]{10}/g, // 10 dígitos
          /(?:\+57\s?)?\(?[0-9]{3}\)?\s?[0-9]{3}\s?[0-9]{4}/g, // Formato con paréntesis
          /(?:\+57\s?)?[0-9]{3}[-\.\s]?[0-9]{3}[-\.\s]?[0-9]{4}/g, // Con separadores
        ];

        for (const pattern of phonePatterns) {
          const match = content.match(pattern);
          if (match) {
            // Limpiar el número extraído (eliminar espacios, guiones, paréntesis)
            const cleanedNumber = match[0].replace(/[\s\-\(\)\.]/g, '');
            userPhoneNumber = cleanedNumber;
            return true;
          }
        }
      }
    }
    return false;
  };

  // Manejar la selección de una ciudad
  const handleCitySelect = (cityName: string) => {
    if (onSendMessage) {
      onSendMessage(cityName);
      setShowCityCards(false);
      citiesShownInCurrentConversation = true;
    }
  };

  // Manejar la selección de una empresa/profesional
  const handleCompanySelect = (companyId: string, companyName: string) => {
    if (onSendMessage) {
      onSendMessage(`He elegido a ${companyName}. Por favor proporciona su información de contacto.`);
      const selectedCompany = companies.find(company => company.companyId === companyId);
      if (selectedCompany) {
        selectedCompanyData = {
          companyId,
          companyName,
          phoneNumber: selectedCompany.phone || '+57 300 123 4567',
          profileImage: selectedCompany.profileImage
        };
      } else {
        selectedCompanyData = {
          companyId,
          companyName,
          phoneNumber: '+57 300 123 4567'
        };
      }

      setShowCompanyCards(false);
      companiesShownInCurrentConversation = true;
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex mb-4">
      <div className="flex flex-col w-full">
        <div className="flex items-start">
          <div className="hidden md:flex w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 items-center justify-center mr-2">
            <span className="text-sm font-bold text-xervix-primary">X</span>
          </div>
          <div className="bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark text-xervix-text-primary dark:text-xervix-text-primary-dark p-3 rounded-tr-lg rounded-br-lg rounded-bl-lg w-full md:max-w-[95%] lg:max-w-[85%] xl:max-w-[80%]">
            <p className="whitespace-pre-wrap break-words mb-2">{content}</p>

            {/* Mostrar cards de ciudades si es necesario */}
            {showCityCards && (
              <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                {loadingCities ? (
                  <div className="flex justify-center py-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-xervix-primary"></div>
                  </div>
                ) : cityError ? (
                  <div className="text-red-500 text-sm">{cityError}</div>
                ) : (
                  <CityCards cities={cities} onCitySelect={handleCitySelect} />
                )}
              </div>
            )}

            {/* Mostrar loader mientras se buscan empresas */}
            {showSearchingLoader && !showCompanyCards && (
              <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-xervix-primary mr-3"></div>
                  <p className="text-sm text-xervix-text-muted dark:text-xervix-text-muted-dark">
                    Buscando profesionales disponibles...
                  </p>
                </div>
              </div>
            )}

            {/* Mostrar cards de empresas/profesionales si es necesario */}
            {showCompanyCards && (
              <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                {loadingCompanies ? (
                  <div className="flex justify-center py-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-xervix-primary"></div>
                  </div>
                ) : companyError ? (
                  <div className="text-red-500 text-sm">{companyError}</div>
                ) : companies.length > 0 ? (
                  <CompanyCards
                    companies={companies}
                    onCompanySelect={handleCompanySelect}
                    conversationHistory={allMessages}
                    serviceName={serviceDescription}
                    categoryName={categoryName}
                    cityName={selectedCity}
                  />
                ) : (
                  <div className="text-sm text-xervix-text-muted dark:text-xervix-text-muted-dark">
                    No se encontraron profesionales disponibles para este servicio en {selectedCity}.
                  </div>
                )}
              </div>
            )}

            {/* Mostrar tarjeta de contacto de la empresa si es necesario */}
            {showContactCard && companyContactInfo && (
              <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                <CompanyContactCard
                  companyName={companyContactInfo.companyName}
                  phoneNumber={companyContactInfo.phoneNumber}
                  profileImage={companyContactInfo.profileImage}
                />
              </div>
            )}

            {contentByDefault && (
              <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                <div className="flex items-center justify-center py-4">
                  <p className="text-sm text-xervix-text-muted dark:text-xervix-text-muted-dark">
                    {contentByDefault}
                  </p>
                </div>
              </div>
            )}

          </div>
        </div>

        <div className="flex mt-1 ml-10 space-x-2">
          <button
            onClick={() => { }}
            className="text-xs text-xervix-text-muted dark:text-xervix-text-muted-dark hover:text-xervix-primary flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Regenerar respuesta
          </button>

          <button
            onClick={handleCopy}
            className="text-xs text-xervix-text-muted dark:text-xervix-text-muted-dark hover:text-xervix-primary flex items-center"
          >
            {copied ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copiado
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copiar
              </>
            )}
          </button>
        </div>

        {timestamp && (
          <span className="text-xs text-xervix-text-muted dark:text-xervix-text-muted-dark ml-10">
            {timestamp}
          </span>
        )}
      </div>
    </div>
  );
};

export default AgentMessage;
